import type { ReactElement } from "react";
import { formatAssetDisplay } from "./display";
import { useAssetEntry } from "./hooks";

export interface AssetNameProps {
    /**
     * The asset id to resolve. Empty / nullish renders an em-dash so
     * detail panels can render the component unconditionally even when
     * the underlying field is optional.
     */
    readonly id: string | null | undefined;
}

/**
 * Display-only component that renders an asset reference as
 * "ASS-024 · Laptop · Dell Latitude 7440 (7832e919-…)", resolving the
 * id via the shared asset lookup cache. Falls back to the bare id while
 * the lookup is pending or when the asset has been hard-deleted.
 *
 * Use this in form/detail views that show a single assetId. List views
 * that already render asset metadata inline (e.g. AssetsList) don't
 * need it — the row already knows its own asset.
 */
export function AssetName({ id }: AssetNameProps): ReactElement {
    const trimmed = typeof id === "string" ? id.trim() : "";
    const query = useAssetEntry(trimmed === "" ? null : trimmed);

    if (trimmed === "") {
        return <span>—</span>;
    }

    return <span>{formatAssetDisplay(query.data ?? null, trimmed)}</span>;
}
